import { ClipboardList, Star } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { useTranslation } from 'react-i18next';

// Helper pour extraire les valeurs RGB d'une couleur (supporte hex et rgb)
function parseRgbColor(color) {
    if (!color) return { r: 74, g: 144, b: 226 };

    // Format rgb(r, g, b)
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
        return { r: rgbMatch[1], g: rgbMatch[2], b: rgbMatch[3] };
    }

    // Format hex #RRGGBB ou #RGB
    const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (hexMatch) {
        return {
            r: parseInt(hexMatch[1], 16),
            g: parseInt(hexMatch[2], 16),
            b: parseInt(hexMatch[3], 16),
        };
    }

    // Format hex court #RGB
    const shortHexMatch = color.match(/^#?([a-f\d])([a-f\d])([a-f\d])$/i);
    if (shortHexMatch) {
        return {
            r: parseInt(shortHexMatch[1] + shortHexMatch[1], 16),
            g: parseInt(shortHexMatch[2] + shortHexMatch[2], 16),
            b: parseInt(shortHexMatch[3] + shortHexMatch[3], 16),
        };
    }

    return { r: 74, g: 144, b: 226 }; // Fallback
}

/**
 * @param {object} props
 * @param {object} props.procedure - { uniq_id, name, color, abbreviation, description }
 * @param {function} props.onClick
 * @param {boolean} [props.isAdmin=false] - Procédure-type (admin) : badge + hover bleu
 * @param {string} [props.companyTag] - Abréviation de l'entreprise pour le badge admin (ex: "O54")
 * @param {boolean} [props.isFavorite=false] - Si la procédure est en favori
 * @param {function} [props.onToggleFavorite] - Callback pour toggler le favori
 */
export default function ProcedureCard({
    procedure,
    onClick,
    isAdmin = false,
    companyTag,
    isFavorite = false,
    onToggleFavorite,
}) {
    const { t } = useTranslation();
    const rgb = parseRgbColor(procedure.color);
    const gradientStyle = {
        background: `linear-gradient(to right bottom, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.082), rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03))`,
    };

    return (
        <div
            onClick={onClick}
            className={cn(
                'bg-card rounded-[12px] border border-border overflow-hidden flex flex-col transition-all duration-200 relative cursor-pointer hover:shadow-lg hover:scale-[1.02]',
                isAdmin ? 'hover:border-badge-invitation' : 'hover:border-secondary',
            )}
            style={gradientStyle}
        >
            {/* Badge admin */}
            {isAdmin && companyTag && (
                <div className="absolute top-[8px] left-[8px] z-10 flex items-center gap-[3px] bg-badge-invitation/10 px-[6px] py-[3px] rounded-full">
                    <ClipboardList className="h-2.5 w-2.5 text-badge-invitation" />
                    <span className="text-[9px] text-badge-invitation">{companyTag}</span>
                </div>
            )}

            {/* Star favorite button */}
            {onToggleFavorite && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(procedure.uniq_id);
                    }}
                    className="absolute top-[8px] right-[8px] z-10 p-[4px] rounded-full hover:bg-black/5 transition-colors"
                >
                    <Star
                        className={cn(
                            'h-4 w-4 transition-colors',
                            isFavorite ? 'fill-warning text-warning' : 'text-muted-foreground',
                        )}
                    />
                </button>
            )}

            <div className="w-full flex flex-col items-center justify-center px-[20px] py-[24px]">
                {/* Icon Circle */}
                <div
                    className="rounded-full h-[56px] w-[56px] flex items-center justify-center mb-[12px] shadow-md"
                    style={{ backgroundColor: procedure.color }}
                >
                    <span className="text-[20px] text-primary-foreground font-semibold">{procedure.abbreviation}</span>
                </div>

                {/* Procedure Name */}
                <p className="text-[14px] text-foreground text-center leading-tight font-medium mb-[8px]">
                    {procedure.name}
                </p>

                {/* Description */}
                {procedure.description && (
                    <p className="text-[13px] text-muted-foreground text-center line-clamp-2">
                        {procedure.description}
                    </p>
                )}
            </div>
        </div>
    );
}
